import { Field, FormikHelpers } from "formik";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useModal } from "hooks/useModal";
import { formatDate } from "utils/formatDate";
import * as Yup from "yup";

// Form validation schema
const lockDateSchema = Yup.object().shape({
  accounting_lock_date: Yup.string().required("Lock date is required"),
});

// Form values interface
type LockDateFormValues = {
  accounting_lock_date: string;
};

interface EditLockDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  lockDateData: any;
  formValues: LockDateFormValues;
  onSubmit: (
    values: LockDateFormValues,
    formikHelpers: FormikHelpers<LockDateFormValues>
  ) => Promise<void>;
  isUpdating: boolean;
}

export const EditLockDateModal = ({
  isOpen,
  onClose,
  lockDateData,
  formValues,
  onSubmit,
  isUpdating,
}: EditLockDateModalProps) => {
  const confirmModal = useModal();

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content>
        <FormikForm
          initialValues={formValues || { accounting_lock_date: "" }}
          validationSchema={lockDateSchema}
          // @ts-ignore
          onSubmit={onSubmit}
          enableReinitialize
        >
          {({
            values,
            isSubmitting,
            errors,
            isValid,
            validateForm,
            submitForm,
          }) => (
            <>
              <Modal.Header>
                <Modal.Title>
                  {lockDateData?.accounting_lock_date
                    ? "Edit Lock Date"
                    : "Enter Lock Date"}
                </Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <div className="t-flex t-flex-col t-gap-4">
                  <Field name="accounting_lock_date">
                    {({ field, form }: any) => (
                      <DateInput
                        label="Lock date"
                        {...field}
                        portalId={field.name}
                        maxDate={new Date()}
                        placeholder="DD-MMM-YYYY"
                      />
                    )}
                  </Field>
                </div>
              </Modal.Body>
              <Modal.Footer className="t-flex t-justify-end t-gap-2">
                {/* Confirm Lock Date Modal */}
                <Modal.Root
                  open={confirmModal.isOpen}
                  onOpenChange={() => {
                    confirmModal.close();
                    onClose();
                  }}
                >
                  <Modal.Content>
                    <Modal.Header>
                      <Modal.Title>
                        {lockDateData?.accounting_lock_date
                          ? "Edit Lock Date?"
                          : "Lock Financials?"}
                      </Modal.Title>
                      <Modal.Close />
                    </Modal.Header>
                    <Modal.Body>
                      <p className="t-text-body-sm t-text-text-60 t-mb-0">
                        {lockDateData?.accounting_lock_date ? (
                          <span>
                            If you proceed, you or anyone won't be able to make
                            any data affecting transactions on or before{" "}
                            <span className="t-font-bold">
                              {values?.accounting_lock_date
                                ? formatDate(values.accounting_lock_date)
                                : ""}
                            </span>
                            .
                          </span>
                        ) : (
                          <span>
                            If you proceed, no edits will be allowed that impact
                            the financials on or before{" "}
                            <span className="t-font-bold">
                              {values?.accounting_lock_date
                                ? formatDate(values.accounting_lock_date)
                                : ""}
                            </span>
                          </span>
                        )}
                      </p>
                      <p className="t-mt-4 t-text-body-sm t-text-text-60 t-mb-0">
                        Do you want to continue?
                      </p>
                      <p className="t-mt-4 t-text-body-italic t-italic t-text-text-60 t-mb-0 sm:t-text-body-sm-italic">
                        Note: If you need to make any changes later, you can
                        unlock the financials at any time.
                      </p>
                    </Modal.Body>
                    <Modal.FooterButtonGroup>
                      <Button onClick={confirmModal.close} type="button">
                        Go back
                      </Button>
                      <Button
                        customType="danger"
                        onClick={submitForm}
                        isLoading={isSubmitting || isUpdating}
                        disabled={isSubmitting || isUpdating}
                      >
                        Confirm
                      </Button>
                    </Modal.FooterButtonGroup>
                  </Modal.Content>
                </Modal.Root>

                <Button onClick={() => onClose()} type="button">
                  Cancel
                </Button>
                <Button
                  type="button"
                  customType="primary"
                  onClick={async () => {
                    try {
                      const errors = await validateForm();
                      if (Object.keys(errors).length > 0) {
                        return;
                      }
                      confirmModal.open();
                    } catch (error) {}
                  }}
                  disabled={!isValid}
                >
                  {lockDateData?.accounting_lock_date ? "Next" : "Proceed"}
                </Button>
              </Modal.Footer>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
